import { useRef, useState } from 'react';
import type { TicketBoardFilter } from '@brimo/shared';
import { FilterChip, MenuItem, MenuList, Popover } from '../../../ds/components';
import { clsx } from '../../../ds/clsx';
import type { Named, TicketBoard } from '../types';
import styles from './board.module.css';

export interface FilterBarProps {
  board: TicketBoard;
  categoryId: string | null;
  assigneeId: string | null;
  onFilter: (key: TicketBoardFilter) => void;
  onCategory: (id: string | null) => void;
  onAssignee: (id: string | null) => void;
}

/**
 * Filter row of Backoffice 03/04: the pills from the API (`?filter=`) and on the right the two select chips
 * „Kategorie" / „Zuständig" (`?categoryId=`, `?assigneeId=`). A chosen value replaces the chip's label.
 */
export function FilterBar({
  board,
  categoryId,
  assigneeId,
  onFilter,
  onCategory,
  onAssignee,
}: FilterBarProps) {
  return (
    <div className={styles.filters}>
      <div className={styles.pills} role="group" aria-label="Filter">
        {board.pills.map((pill) => (
          <FilterChip
            key={pill.key}
            label={pill.label}
            count={pill.count.toLocaleString('de-DE')}
            active={pill.active}
            className={clsx(styles.boardPill, pill.active && styles.activePill)}
            onClick={() => onFilter(pill.key)}
          />
        ))}
      </div>
      <div className={styles.selects}>
        <SelectChip
          label="Kategorie"
          allLabel="Alle Kategorien"
          options={board.categories}
          value={categoryId}
          onChange={onCategory}
        />
        <SelectChip
          label="Zuständig"
          allLabel="Alle Zuständigen"
          options={board.assignees}
          value={assigneeId}
          onChange={onAssignee}
        />
      </div>
    </div>
  );
}

interface SelectChipProps {
  label: string;
  allLabel: string;
  options: Named[];
  value: string | null;
  onChange: (id: string | null) => void;
}

function SelectChip({ label, allLabel, options, value, onChange }: SelectChipProps) {
  const anchorRef = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);
  const selected = options.find((option) => option.id === value) ?? null;
  const choose = (id: string | null) => {
    setOpen(false);
    onChange(id);
  };
  return (
    <span ref={anchorRef} className={styles.selectAnchor}>
      <FilterChip
        label={selected ? selected.name : label}
        active={selected !== null}
        aria-label={selected ? `${label}: ${selected.name}` : label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      />
      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={anchorRef}
        placement="bottom-end"
        width={220}
        variant="menu"
        initialFocus="none"
      >
        <MenuList label={label} density="compact" onClose={() => setOpen(false)}>
          <MenuItem label={allLabel} active={value === null} onSelect={() => choose(null)} />
          {options.map((option) => (
            <MenuItem
              key={option.id}
              label={option.name}
              active={option.id === value}
              onSelect={() => choose(option.id)}
            />
          ))}
        </MenuList>
      </Popover>
    </span>
  );
}
